import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@wtc/ui";
import { getSupabase } from "@/lib/hooks/api/utils";

// ============================================================================
// USER ACCESS GRANTS — READ HOOK
// ============================================================================
//
// Read-only query for the User Details modal "Access & Permissions" tab.
// Lists a user's CURRENT contact-access grants:
//   - employee_contact_access (user_id -> contact_id) for WTC employees
//   - contact_user_access     (user_id -> contact_id + access_level) for client users
//
// This is a NEW read hook scoped to the modal. It deliberately does NOT touch
// the grant/revoke mutation hooks in lib/hooks/api/admin/* (owned elsewhere).
// ============================================================================

export interface AccessibleContact {
  /** Row id of the access grant (employee_contact_access or contact_user_access). */
  grantId: string;
  contactId: string;
  contactName: string | null;
  contactEmail: string | null;
  companyId: string | null;
  companyName: string | null;
  /** Only present for client-user (contact_user_access) grants. */
  accessLevel?: string | null;
}

export interface AccessibleCompany {
  companyId: string;
  companyName: string | null;
  accessLevel: string | null;
}

export interface UserAccessGrants {
  /** employee_contact_access rows (employee can access these contacts). */
  employeeGrants: AccessibleContact[];
  /** contact_user_access rows (client user is scoped to these contacts). */
  clientGrants: AccessibleContact[];
  /** company_user_access rows (client user — company-wide scope). */
  companyGrants: AccessibleCompany[];
}

interface ContactJoin {
  id: string;
  name: string | null;
  email: string | null;
  company_id: string | null;
  company_name: string | null;
}

interface EmployeeAccessRow {
  id: string;
  contact_id: string;
  contacts: ContactJoin | ContactJoin[] | null;
}

interface ClientAccessRow {
  id: string;
  contact_id: string;
  access_level: string | null;
  is_active: boolean | null;
  contacts: ContactJoin | ContactJoin[] | null;
}

interface CompanyAccessRow {
  company_id: string;
  access_level: string | null;
  is_active: boolean | null;
  companies: { id: string; name: string | null } | { id: string; name: string | null }[] | null;
}

function pickContact(
  contacts: ContactJoin | ContactJoin[] | null | undefined
): ContactJoin | null {
  if (!contacts) return null;
  return Array.isArray(contacts) ? (contacts[0] ?? null) : contacts;
}

function pickCompany(
  companies: { id: string; name: string | null } | { id: string; name: string | null }[] | null | undefined
): { id: string; name: string | null } | null {
  if (!companies) return null;
  return Array.isArray(companies) ? (companies[0] ?? null) : companies;
}

/**
 * Fetch the contact-access grants for a single user (both employee and client
 * shapes). Returns empty arrays — never throws to the caller's render — and is
 * disabled until authenticated + a userId is provided.
 */
export function useUserAccessGrants(userId: string | undefined) {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const supabase = getSupabase();

  return useQuery({
    queryKey: ["user-access-grants", userId],
    queryFn: async (): Promise<UserAccessGrants> => {
      if (!isAuthenticated || !appUser || !userId) {
        return { employeeGrants: [], clientGrants: [], companyGrants: [] };
      }

      const [employeeRes, clientRes, companyRes] = await Promise.all([
        supabase
          .from("employee_contact_access")
          .select(
            `
            id,
            contact_id,
            contacts:contact_id (
              id,
              name,
              email,
              company_id,
              company_name
            )
          `
          )
          .eq("user_id", userId),
        supabase
          .from("contact_user_access")
          .select(
            `
            id,
            contact_id,
            access_level,
            is_active,
            contacts:contact_id (
              id,
              name,
              email,
              company_id,
              company_name
            )
          `
          )
          .eq("user_id", userId),
        supabase
          .from("company_user_access")
          .select(
            `
            company_id,
            access_level,
            is_active,
            companies:company_id (
              id,
              name
            )
          `
          )
          .eq("user_id", userId),
      ]);

      if (employeeRes.error) {
        throw new Error(
          `Failed to load employee access grants: ${employeeRes.error.message}`
        );
      }
      if (clientRes.error) {
        throw new Error(
          `Failed to load client access grants: ${clientRes.error.message}`
        );
      }
      if (companyRes.error) {
        throw new Error(
          `Failed to load company access grants: ${companyRes.error.message}`
        );
      }

      const employeeGrants: AccessibleContact[] = (
        (employeeRes.data as EmployeeAccessRow[] | null) ?? []
      ).map((row) => {
        const contact = pickContact(row.contacts);
        return {
          grantId: row.id,
          contactId: row.contact_id,
          contactName: contact?.name ?? null,
          contactEmail: contact?.email ?? null,
          companyId: contact?.company_id ?? null,
          companyName: contact?.company_name ?? null,
        };
      });

      const clientGrants: AccessibleContact[] = (
        (clientRes.data as ClientAccessRow[] | null) ?? []
      )
        // Only surface active grants — a revoked grant sets is_active = false.
        .filter((row) => row.is_active !== false)
        .map((row) => {
          const contact = pickContact(row.contacts);
          return {
            grantId: row.id,
            contactId: row.contact_id,
            contactName: contact?.name ?? null,
            contactEmail: contact?.email ?? null,
            companyId: contact?.company_id ?? null,
            companyName: contact?.company_name ?? null,
            accessLevel: row.access_level ?? null,
          };
        });

      const companyGrants: AccessibleCompany[] = (
        (companyRes.data as CompanyAccessRow[] | null) ?? []
      )
        .filter((row) => row.is_active !== false)
        .map((row) => {
          const company = pickCompany(row.companies);
          return {
            companyId: row.company_id,
            companyName: company?.name ?? null,
            accessLevel: row.access_level ?? null,
          };
        });

      return { employeeGrants, clientGrants, companyGrants };
    },
    enabled: isAuthenticated && !!appUser && !!userId,
    staleTime: 60 * 1000,
  });
}
